import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import {
  checkLegacyConversion,
  convertLegacyTournament,
} from '../../../database/tournamentConversion';
import { handleFirebaseError } from '../../../utils/errorHandler';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { FORMAT_LABELS } from '../shell/tournamentTools';

// Lets a super admin switch a result-free legacy tournament to the staged
// format. Checks for blockers on open; only offers the switch when there are
// none.
const ConvertLegacyDialog = ({ open, onOpenChange, tournament, status }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [blockers, setBlockers] = useState([]);
  const [playerCount, setPlayerCount] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    checkLegacyConversion(tournament.id, status)
      .then((result) => {
        if (cancelled) return;
        setBlockers(result.blockers);
        setPlayerCount(result.playerCount);
      })
      .catch((err) => {
        if (!cancelled) setError(handleFirebaseError(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, tournament.id, status]);

  const label = FORMAT_LABELS[tournament.format || 'leaderboard'] || tournament.format;
  const blocked = blockers.length > 0;

  const handleConvert = async () => {
    setBusy(true);
    try {
      await convertLegacyTournament(tournament);
      toast.success('Switched to stages.');
      onOpenChange(false);
    } catch (err) {
      toast.error('Could not switch format.', { description: handleFirebaseError(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {loading
              ? 'Checking…'
              : blocked
                ? "This tournament can't switch"
                : 'Switch to the new format?'}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <p className="text-sm text-muted-foreground">Checking for results…</p>
        ) : error ? (
          <p className="text-sm text-destructive">{error}</p>
        ) : blocked ? (
          <ul className="list-disc space-y-1 pl-5 text-sm text-destructive">
            {blockers.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
        ) : (
          <div className="space-y-4 text-sm">
            <div>
              <div className="mb-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                Kept
              </div>
              <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
                <li>Name, rules, banner and logo</li>
                <li>Dates, signup link</li>
                <li>Sponsors and prize pool</li>
                <li>
                  {playerCount} player{playerCount === 1 ? '' : 's'}
                </li>
                <li>Maps</li>
              </ul>
            </div>
            <div>
              <div className="mb-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                Changes
              </div>
              <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
                <li>The old {label} settings are removed.</li>
                <li>One Leaderboard stage is created. Set up the stages next.</li>
                <li>Every public section except the rules is hidden until you switch it on.</li>
                <li>This can't be undone.</li>
              </ul>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          {!loading && !error && !blocked && (
            <Button type="button" onClick={handleConvert} disabled={busy}>
              {busy ? 'Switching…' : 'Switch to stages'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConvertLegacyDialog;
